import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { 
  User, 
  Mail, 
  Building, 
  Lock, 
  Eye, 
  EyeOff, 
  ArrowRight, 
  Check,
  Star,
  Shield,
  CheckCircle,
  Users
} from 'lucide-react';

const SignupPage = () => {
  const { register } = useAuth();
  const { addNotification } = useNotifications();
  const [searchParams] = useSearchParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(searchParams.get('plan') || 'essential');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United Kingdom',
    agreeToTerms: false
  });

  const plans = [
    {
      id: 'essential',
      name: 'Essential',
      price: '39.99',
      description: 'Perfect for small businesses getting started',
      features: [
        'Send and receive B2B invoices',
        'Automatic invoice approval (7-day rule)',
        'Automatic payment collection',
        'Track payment statuses',
        'Basic analytics dashboard',
        '1 user account'
      ],
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '49.99',
      description: 'For growing businesses that need more power',
      features: [
        'All Essential features, plus:',
        'Chat functionality for invoices',
        'Up to 5 user accounts',
        'Advanced analytics & insights',
        'Enhanced payment metrics'
      ],
      popular: true
    }
  ];

  const trialBenefits = [
    {
      title: 'Access to Your Selected Plan',
      description: 'Try out all the features of your chosen plan (Essential or Pro) without any limitations.'
    },
    {
      title: 'Quick Setup',
      description: 'Get started in minutes with our simple onboarding process and intuitive interface.'
    },
    {
      title: 'Cancel Anytime',
      description: 'If you decide Receevit® isn\'t for you, you can cancel your subscription at any time.'
    },
    {
      title: 'Export Your Data',
      description: 'If you choose to leave after your trial, you can export all your data for free.'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form - all fields are mandatory
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.companyName || !formData.address || !formData.city || !formData.state || !formData.zipCode || !formData.country) {
      setError('Please fill in all required fields');
      return;
    }

    // SECURITY: Prevent test data from being used in production
    const testDataPatterns = [
      'test company',
      'test company ltd',
      'test',
      'example',
      'sample',
      'demo',
      'placeholder'
    ];
    
    const normalizedCompanyName = formData.companyName.toLowerCase().trim();
    if (testDataPatterns.some(pattern => normalizedCompanyName.includes(pattern))) {
      setError('Please enter your actual company name, not test data');
      return;
    }
    
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (!formData.agreeToTerms) {
      setError('Please agree to the Terms of Use and Privacy Policy');
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Log the data being sent
      const requestData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        companyName: formData.companyName,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
        selectedPlan: selectedPlan
      };
      
      console.log('Sending registration data:', requestData);
      
      // Use AuthContext register function
      const result = await register(requestData);
      
      if (result.success) {
        console.log('Registration successful:', result);
        
        // If Stripe checkout URL is provided, redirect to it
        if (result.checkoutUrl) {
          console.log('Redirecting to Stripe checkout:', result.checkoutUrl);
          window.location.href = result.checkoutUrl;
        } else {
          console.log('No checkout URL, redirecting to success page');
          // Otherwise, redirect to success page
          window.location.href = '/portal/dashboard';
        }
      } else {
        console.error('Registration failed:', result);
        setError(result.error || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError('Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  return (
    <>
      <Helmet>
        <title>Sign Up - Start 30-Day Free Trial | Receevit®</title>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0, user-scalable=yes" />
        <meta name="description" content="Create your Receevit® account and start your 30-day free trial. Automated credit control for B2B invoices. Essential £39.99/mo, Pro £49.99/mo." />
        <link rel="canonical" href="https://www.receevit.com/signup" />
        <meta property="og:title" content="Sign Up - Start 30-Day Free Trial | Receevit®" />
        <meta property="og:description" content="Create your Receevit® account and start your 30-day free trial. Automated credit control for B2B invoices. Essential £39.99/mo, Pro £49.99/mo." />
        <meta property="og:url" content="https://www.receevit.com/signup" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.receevit.com/og-image.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Sign Up - Start 30-Day Free Trial | Receevit®",
            "url": "https://www.receevit.com/signup",
            "description": "Create your Receevit® account and start your 30-day free trial. Automated credit control for B2B invoices.",
            "publisher": {
              "@type": "Organization",
              "name": "Receevit®",
              "url": "https://www.receevit.com/"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://www.receevit.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Sign Up",
                "item": "https://www.receevit.com/signup"
              }
            ]
          })}
        </script>
      </Helmet>
      
      <div className="flex-1 w-full bg-[#1F11ED] pt-6 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{
            x: [0, 100, 0],
            y: [0, -50, 0],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-20 left-20 w-32 h-32 bg-white/10 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            x: [0, -100, 0],
            y: [0, 50, 0],
            rotate: [360, 180, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-40 right-20 w-24 h-24 bg-white/10 rounded-full blur-xl"
        />
        <motion.div
          animate={{
            x: [0, 80, 0],
            y: [0, -80, 0],
            scale: [1, 1.2, 1]
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute bottom-20 left-1/3 w-20 h-20 bg-white/10 rounded-full blur-lg"
        />
      </div>

      <div className="relative max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Create Your Free 30-Day Trial
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Join thousands of businesses already using Receevit® to streamline their B2B invoicing and get paid faster
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Signup Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-2xl"
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}
              
              {/* Plan Selection */}
              <div className="mt-8">
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Choose Your Plan
                </label>
                <div className="grid grid-cols-1 gap-4">
                  {plans.map((plan) => (
                    <label
                      key={plan.id}
                      className={`relative cursor-pointer p-5 rounded-xl border-2 transition-all duration-200 touch-manipulation min-h-[80px] ${
                        selectedPlan === plan.id
                          ? 'border-[#1F11ED] bg-[#1F11ED]/10'
                          : 'border-gray-200 bg-gray-50 hover:border-[#1F11ED]/60 hover:bg-gray-100'
                      }`}
                    >
                      <input
                        type="radio"
                        name="plan"
                        value={plan.id}
                        checked={selectedPlan === plan.id}
                        onChange={(e) => setSelectedPlan(e.target.value)}
                        className="sr-only"
                      />
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-900">{plan.name}</div>
                          <div className="text-sm text-gray-600">{plan.description}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-bold text-gray-900">£{plan.price}</div>
                          <div className="text-sm text-gray-600">/month</div>
                        </div>
                      </div>
                      {selectedPlan === plan.id && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="absolute top-2 right-2 w-5 h-5 bg-[#1F11ED] rounded-full flex items-center justify-center"
                        >
                          <Check className="w-3 h-3 text-white" />
                        </motion.div>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* Form Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    required
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                    placeholder="Enter your first name"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    required
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                  placeholder="Enter your email address"
                />
              </div>

              <div>
                <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="companyName"
                  name="companyName"
                  type="text"
                  required
                  autoComplete="organization"
                  value={formData.companyName}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                  placeholder="Enter your company name"
                />
              </div>

              {/* Address Section */}
              <div>
                <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                  Address <span className="text-red-500">*</span>
                </label>
                <input
                  id="address"
                  name="address"
                  type="text"
                  required
                  autoComplete="street-address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                  placeholder="Street address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    required
                    autoComplete="address-level2"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
                    State/County <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="state"
                    name="state"
                    type="text"
                    required
                    autoComplete="address-level1"
                    value={formData.state}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                    placeholder="State/County"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                    Postcode <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="zipCode"
                    name="zipCode"
                    type="text"
                    required
                    autoComplete="postal-code"
                    inputMode="text"
                    value={formData.zipCode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                    placeholder="Postcode"
                  />
                </div>
                <div>
                  <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-2">
                    Country <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="country"
                    name="country"
                    required
                    autoComplete="country-name"
                    value={formData.country}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                  >
                    <option value="United Kingdom">United Kingdom</option>
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                    <option value="Australia">Australia</option>
                    <option value="Ireland">Ireland</option>
                    <option value="Germany">Germany</option>
                    <option value="France">France</option>
                    <option value="Spain">Spain</option>
                    <option value="Italy">Italy</option>
                    <option value="Netherlands">Netherlands</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      required
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={handleChange}
                      className="w-full px-4 py-3 pr-12 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                      placeholder="Create a password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? "text" : "password"}
                      required
                      autoComplete="new-password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="w-full px-4 py-3 pr-12 bg-white border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#1F11ED] focus:ring-offset-2 focus:ring-offset-white focus:border-transparent transition-all duration-200"
                      placeholder="Confirm your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Terms and Conditions */}
              <div className="flex items-start space-x-3">
                <input
                  id="terms"
                  name="agreeToTerms"
                  type="checkbox"
                  required
                  checked={formData.agreeToTerms}
                  onChange={handleChange}
                  className="h-5 w-5 text-[#1F11ED] focus:ring-[#1F11ED] border-gray-300 rounded bg-white mt-0.5 touch-manipulation"
                />
                <label htmlFor="terms" className="text-sm text-gray-600">
                  I agree to the{' '}
                  <a href="/terms" className="text-[#1F11ED] hover:text-[#1F11ED]/80 underline">
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" className="text-[#1F11ED] hover:text-[#1F11ED]/80 underline">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                disabled={isLoading || !formData.agreeToTerms}
                className="w-full bg-[#1F11ED] text-white py-4 px-6 rounded-xl font-semibold text-lg hover:bg-[#1F11ED]/90 hover:shadow-[0_0_20px_rgba(31,17,237,0.4)] transition-all duration-300 transform hover:scale-105 border-2 border-[#1F11ED] disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation min-h-[48px]"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Creating Account...
                  </div>
                ) : (
                  'Start Free Trial'
                )}
              </motion.button>
            </form>

            {/* Login Link */}
            <div className="mt-6 text-center">
              <p className="text-gray-600">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-[#1F11ED] hover:text-[#1F11ED]/80 transition-colors duration-200 underline"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </motion.div>

          {/* Benefits Sidebar */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="space-y-8"
          >
            {/* Trial Benefits */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">What You Get with Your Free Trial</h3>
              <div className="space-y-4">
                {trialBenefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.title}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <div className="w-6 h-6 bg-gradient-to-r from-[#10B981] to-[#059669] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                      <p className="text-gray-600 text-sm">{benefit.description}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Trust Indicators */}
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Trusted by Growing Businesses</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-[#1F11ED]" />
                  <span className="text-gray-600">Bank-level security & encryption</span>
                </div>
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-5 h-5 text-[#10B981]" />
                  <span className="text-gray-600">GDPR compliant & secure</span>
                </div>
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5 text-[#8B5CF6]" />
                  <span className="text-gray-600">24/7 customer support</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      </div>
    </>
  );
};

export default SignupPage;